import { createContext, useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { jsx, Fragment } from 'react/jsx-runtime';

// src/provider.tsx
var SDKContext = createContext(null);
function SDKProvider({
  sdk,
  children,
  fallback = null,
  onReady,
  onError
}) {
  const [isReady, setIsReady] = useState(sdk.isInitialized());
  const [error, setError] = useState(null);
  useEffect(() => {
    if (sdk.isInitialized()) {
      setIsReady(true);
      onReady?.();
      return;
    }
    let mounted = true;
    sdk.init().then(() => {
      if (mounted) {
        setIsReady(true);
        onReady?.();
      }
    }).catch((err) => {
      if (mounted) {
        const error2 = err instanceof Error ? err : new Error(String(err));
        setError(error2);
        onError?.(error2);
      }
    });
    return () => {
      mounted = false;
    };
  }, [sdk, onReady, onError]);
  useEffect(() => {
    return () => {
    };
  }, [sdk]);
  const contextValue = useMemo(
    () => ({
      sdk,
      isReady
    }),
    [sdk, isReady]
  );
  if (error) {
    throw error;
  }
  if (!isReady) {
    return /* @__PURE__ */ jsx(Fragment, { children: fallback });
  }
  return /* @__PURE__ */ jsx(SDKContext.Provider, { value: contextValue, children });
}
function useSDK() {
  const context = useContext(SDKContext);
  if (!context) {
    throw new Error("useSDK must be used within an SDKProvider");
  }
  return context.sdk;
}
function useSDKReady() {
  const context = useContext(SDKContext);
  return context?.isReady ?? false;
}
function useAuth() {
  const sdk = useSDK();
  const auth = sdk.get("auth");
  if (!auth) {
    throw new Error(
      "useAuth requires @godgpt/auth module. Register AuthModule before using this hook."
    );
  }
  const [user, setUser] = useState(auth.getCurrentUser());
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    const unsubscribeSignIn = sdk.on("auth:signIn", (...args) => {
      const data = args[0];
      setUser(data.user);
    });
    const unsubscribeSignOut = sdk.on("auth:signOut", () => {
      setUser(null);
    });
    const unsubscribeSessionRestored = sdk.on(
      "auth:sessionRestored",
      (...args) => {
        const data = args[0];
        setUser(data.user);
      }
    );
    const unsubscribeUserUpdated = sdk.on(
      "auth:userUpdated",
      (...args) => {
        const data = args[0];
        setUser(data.user);
      }
    );
    return () => {
      unsubscribeSignIn();
      unsubscribeSignOut();
      unsubscribeSessionRestored();
      unsubscribeUserUpdated();
    };
  }, [sdk]);
  const signIn = useCallback(
    async (provider) => {
      setIsLoading(true);
      try {
        return await auth.signIn(provider);
      } finally {
        setIsLoading(false);
      }
    },
    [auth]
  );
  const signOut = useCallback(async () => {
    setIsLoading(true);
    try {
      await auth.signOut();
    } finally {
      setIsLoading(false);
    }
  }, [auth]);
  const getAccessToken = useCallback(() => {
    return auth.getAccessToken();
  }, [auth]);
  const updateUser = useCallback(
    async (userData) => {
      await auth.updateUser(userData);
    },
    [auth]
  );
  return useMemo(
    () => ({
      user,
      isAuthenticated: auth.isAuthenticated(),
      isLoading,
      signIn,
      signOut,
      getAccessToken,
      updateUser
    }),
    [user, isLoading, signIn, signOut, getAccessToken, updateUser, auth]
  );
}
function useTranslation() {
  const sdk = useSDK();
  const i18n = sdk.get("i18n");
  if (!i18n) {
    throw new Error(
      "useTranslation requires @godgpt/i18n module. Register I18nModule before using this hook."
    );
  }
  const [locale, setLocaleState] = useState(i18n.getLocale());
  const [isLoading, setIsLoading] = useState(false);
  const [, forceUpdate] = useState({});
  useEffect(() => {
    const unsubscribe = sdk.on("i18n:localeChanged", (...args) => {
      const data = args[0];
      setLocaleState(data.locale);
      forceUpdate({});
    });
    const unsubscribeLoadStart = sdk.on("i18n:loadStart", () => {
      setIsLoading(true);
    });
    const unsubscribeLoadComplete = sdk.on("i18n:loadComplete", () => {
      setIsLoading(false);
      forceUpdate({});
    });
    const unsubscribeLoadError = sdk.on("i18n:loadError", () => {
      setIsLoading(false);
    });
    return () => {
      unsubscribe();
      unsubscribeLoadStart();
      unsubscribeLoadComplete();
      unsubscribeLoadError();
    };
  }, [sdk]);
  const t = useCallback(
    (key, options) => {
      return i18n.t(key, options);
    },
    [i18n, locale]
    // Re-create when locale changes
  );
  const setLocale = useCallback(
    async (newLocale) => {
      setIsLoading(true);
      try {
        await i18n.setLocale(newLocale);
      } finally {
        setIsLoading(false);
      }
    },
    [i18n]
  );
  const exists = useCallback(
    (key) => {
      return i18n.exists(key);
    },
    [i18n, locale]
  );
  return useMemo(
    () => ({
      t,
      locale,
      setLocale,
      supportedLocales: i18n.getSupportedLocales(),
      isLoading,
      exists
    }),
    [t, locale, setLocale, i18n, isLoading, exists]
  );
}
var useI18n = useTranslation;
function useApi() {
  const sdk = useSDK();
  const api = sdk.get("api");
  if (!api) {
    throw new Error(
      "useApi requires @godgpt/api module. Register ApiModule before using this hook."
    );
  }
  const get = useCallback(
    (path, options) => {
      return api.get(path, options);
    },
    [api]
  );
  const post = useCallback(
    (path, body, options) => {
      return api.post(path, body, options);
    },
    [api]
  );
  const put = useCallback(
    (path, body, options) => {
      return api.put(path, body, options);
    },
    [api]
  );
  const patch = useCallback(
    (path, body, options) => {
      return api.patch(path, body, options);
    },
    [api]
  );
  const del = useCallback(
    (path, options) => {
      return api.delete(path, options);
    },
    [api]
  );
  const request = useCallback(
    (method, path, body, options) => {
      const response = api.requestWithResponse(method, path, body, options);
      return response.then((r) => r.data);
    },
    [api]
  );
  return useMemo(
    () => ({
      get,
      post,
      put,
      patch,
      del,
      request
    }),
    [get, post, put, patch, del, request]
  );
}
function useApiRequest() {
  const [state, setState] = useState({
    data: null,
    error: null,
    isLoading: false
  });
  const execute = useCallback(async (fn) => {
    setState({ data: null, error: null, isLoading: true });
    try {
      const data = await fn();
      setState({ data, error: null, isLoading: false });
      return data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      setState({ data: null, error: err, isLoading: false });
      return null;
    }
  }, []);
  const reset = useCallback(() => {
    setState({ data: null, error: null, isLoading: false });
  }, []);
  return useMemo(
    () => ({
      ...state,
      execute,
      reset
    }),
    [state, execute, reset]
  );
}
function useEvent(event, callback) {
  const sdk = useSDK();
  useEffect(() => {
    const unsubscribe = sdk.on(event, callback);
    return unsubscribe;
  }, [sdk, event, callback]);
}
function useEmit() {
  const sdk = useSDK();
  return useCallback(
    (event, ...args) => {
      sdk.emit(event, ...args);
    },
    [sdk]
  );
}

export { SDKProvider, useApi, useApiRequest, useAuth, useEmit, useEvent, useI18n, useSDK, useSDKReady, useTranslation };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map